import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMembership } from "@/contexts/MembershipContext";
import { NameField } from "./fields/NameField";
import { AddressFields } from "./fields/AddressFields";
import { PhoneField } from "./fields/PhoneField";
import { BirthdayField } from "./fields/BirthdayField";
import { GenderField } from "./fields/GenderField";
import { NotesField } from "./fields/NotesField";
import { EmailField } from "./fields/EmailField";
import { LanguageField } from "./fields/LanguageField";
import { AnxiousPatientField } from "./fields/AnxiousPatientField";
import { TranslationType } from "./types";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormReturn,
} from "react-hook-form";
import { FormValues } from "./types";
import { MedicalConsentField } from "./fields/MedicalConsentField.tsx";
import PaymentInfo from "@/components/booking-form/PaymentInfo.tsx";
import { MemberPaymentSummary } from "@/components/booking-form/MemberPaymentSummary";
import { getUrlParam, getTestModeParams } from "@/utils/urlUtils";
import { InvitationCodeInput } from "./InvitationCodeInput";
import { Product } from "@/components/product-step/types";
import { languageMap } from "@/i18n";
import { BookingLanguage } from "@/types/booking";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import {
  isMembershipEligible,
  isBloodMembershipBaseProduct,
  isScanPackage,
  bundleScanContributionCents,
  bundleAddOnCents,
} from "@/utils/bloodMembershipPricing";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketHasMembership } from "@/config/market";
import { useBloodCheckout } from "@/hooks/useBloodCheckout";
import type { Country } from "react-phone-number-input";

// Define the FormFieldsProps interface
interface FormFieldsProps {
  register: UseFormRegister<FormValues>;
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  t: TranslationType;
  validateBirthday: (value: string) => boolean;
  userName?: string;
  userEmail?: string;
  selectedClinic?: string;
  language?: BookingLanguage;
  form?: UseFormReturn<FormValues>;
  selectedProductDetails?: Product[];
}

export const FormFields = ({
  register,
  control,
  errors,
  t,
  validateBirthday,
  userName,
  userEmail,
  form,
  language,
  selectedProductDetails = [],
}: FormFieldsProps) => {
  const lng = languageMap[language || "en-US"];
  const { t: i18n } = useTranslation(undefined, { lng });
  const { isMember, membership, getEffectivePrice } = useMembership();
  const { isGermany, defaultCountryCode, market } = useMarket();
  const { data: featureFlags } = useFeatureFlags();
  const membershipFlowEnabled =
    marketHasMembership(market) && (featureFlags?.["membership_v2"] ?? true);
  const phonePlaceholder = isGermany ? "+49 XXX XXXXXXXX" : "+41 XX XXX XX XX";
  const lockedFirstName = isMember && !!membership?.first_name;
  const lockedLastName = isMember && !!membership?.last_name;
  const lockedEmail = isMember && !!membership?.email;

  // Offer the "become a member & pay now" toggle (path B) as a last-ditch upsell.
  // Eligible for blood-only carts with a real saving AND for scan carts (picking
  // "yearly" here forms the bundle — priced by isBundleCart downstream, charged as
  // the netted scan + cadence subscription by useBloodCheckout).
  const offerMembership = isMembershipEligible(selectedProductDetails);
  // In the membership flow (path A) the member choice was already made up front,
  // so there is no "pay later vs become a member" toggle here — payment happens
  // on the next page (Stripe). The toggle is only for the one-off upsell (path B).
  const membershipPlanActive = useMembershipPlanStore((s) => s.active);
  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);

  // Check if internal mode is enabled via URL parameter
  const isInternal = getUrlParam("internal") === "true";

  // Check if selected products include a blood draw
  const hasBloodWork = selectedProductDetails.some(
    (p) => p.includes_blood_draw,
  );

  // The chosen membership cadence tier (once/twice/four) — its Stripe product id
  // and price. Needed so coupons scoped to the membership tier validate.
  const { cadenceTierProductId, expectedMembershipCents } = useBloodCheckout();

  // Extract items for discount calculation. Send unit_amount so the voucher
  // function computes a percentage discount from the price WE already have —
  // it can't retrieve the product itself because stripe_product_id is the prod
  // ID and staging/sandbox runs a different Stripe account ("No such product").
  // Swap the base blood test for the membership tier in the voucher base ONLY
  // when the user is actually buying the membership — the cadence flow (path A)
  // or "become a member" in the one-off upsell (path B). Reasons:
  //  - the base test IS the membership tier, so counting both double-charges the
  //    CHF 299 (a 10% code came out ~93 on a 626 cart instead of ~63), and
  //  - for "Pay later" the cart is billed at full price, so a non-baseline
  //    cadence (twice/four) must NOT inflate the base above the actual cart.
  // An existing member never buys a (second) membership — Path B's "yearly"
  // selection must not swap the membership tier into their voucher base.
  const buyingMembership =
    !isMember && (membershipPlanActive || selectedPaymentMode === "yearly");
  const includeMembershipTier =
    membershipFlowEnabled &&
    offerMembership &&
    buyingMembership &&
    !!cadenceTierProductId;

  // The voucher base must be the amounts that will actually be BILLED, not the
  // list prices — display, record and charge have to come from the same model
  // (memberOptionPricing's rule). Sending list prices here made the validator
  // quote a discount Stripe never applied: Diana10 on a member cart was quoted
  // 10% of the 684 list base (68.40) while Stripe applied 10% of the 626 member
  // base (62.60), so the customer paid more than the displayed total. It also
  // ran minimum_amount checks against a base bigger than the invoice.
  const voucherUnitAmount = (p: Product): number => {
    if (isMember) return getEffectivePrice(p);
    // Keyed on the tier swap, not buyingMembership alone: only when the tier
    // actually replaces the base test does checkout bill the bundle model.
    if (includeMembershipTier) {
      // The same per-item amounts computeBundlePricing bills: netted scan,
      // member-priced add-ons (the base test is swapped for the tier below).
      return isScanPackage(p)
        ? bundleScanContributionCents(p)
        : bundleAddOnCents(p);
    }
    // Pay later: the cart is billed at full price.
    return p.default_price?.unit_amount ?? 0;
  };

  const items = [
    ...selectedProductDetails
      .filter((p) => p.stripe_product_id)
      .filter(
        (p) => !(includeMembershipTier && isBloodMembershipBaseProduct(p)),
      )
      .map((p) => ({
        stripe_product_id: p.stripe_product_id!,
        unit_amount: voucherUnitAmount(p),
      })),
    // Include the selected membership tier so tier-scoped coupons (whose
    // applies_to lists the cadence products) pass eligibility — the tier is a
    // subscription, not a selectedProduct, so it isn't in the cart items.
    ...(includeMembershipTier
      ? [
          {
            stripe_product_id: cadenceTierProductId,
            unit_amount: expectedMembershipCents,
          },
        ]
      : []),
  ];

  // The payment block (pay-later / become-a-member) only shows in the one-off
  // path B; existing members and the membership flow skip it. Swiss + flag gated
  // so DE / flag-off never renders the dev toggle or inline variant-4 payment.
  const showPaymentBlock =
    membershipFlowEnabled && !isMember && !membershipPlanActive;

  // The other half of that condition. Whichever door attached the membership —
  // "Book as a member" on a scan card, or the membership-first funnel picking a
  // scan later — there is nothing left to choose here, but hiding the section
  // entirely left those users on the last step with a subscription in the cart
  // and no statement of what they were paying. They get a one-card summary
  // instead of a picker. (This used to exclude membership-first on the theory
  // that payment was its own step there; it isn't — the cadence step picks a
  // frequency, and nothing ever stated the charge.)
  const showMemberPaymentSummary =
    membershipFlowEnabled && !isMember && membershipPlanActive;

  const setSelectedPaymentMode = useBookingFlowStore(
    (s) => s.setSelectedPaymentMode,
  );

  // COMPLIANCE: the payment options must start with NOTHING pre-selected — no
  // pre-ticked paid membership. Clear the selection so the basket shows the
  // plain cart (no membership) until the user actively picks an option.
  // `selectedPaymentMode` remains the single flag the basket + cards read, so
  // once the user picks, every surface follows it.
  useEffect(() => {
    if (showPaymentBlock && offerMembership) {
      setSelectedPaymentMode(null);
    } else if (selectedPaymentMode === null) {
      setSelectedPaymentMode("yearly");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentBlock, offerMembership]);
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  // A run seeded with an identity never gets the dev filler. The button is a
  // fixed overlay pinned above the form, so a click aimed at a control beneath
  // it (the DE consent checkboxes sit bottom-left once the step is scrolled
  // down) lands on the button instead and rewrites who is booking. AHD-9698:
  // that is exactly how a release browser test submitted a booking as John Doe
  // while keeping the seeded email.
  const { testUserId, userInformation } = getTestModeParams();
  const isSeededRun = !!(testUserId || userInformation);

  // Dev hosts where testing helpers are allowed: localhost + Vercel previews/
  // staging (*.vercel.app). NEVER a real production domain.
  const isDevHost =
    !isSeededRun &&
    (isLocalhost ||
      (typeof window !== "undefined" &&
        window.location.hostname.endsWith(".vercel.app")));

  // Dev-only: fill the whole details form with valid fictional data so you don't
  // retype it on every preview. Market-aware (CH vs DE) so the values validate.
  const fillTestData = () => {
    if (!form) return;
    const set = (name: keyof FormValues, value: FormValues[keyof FormValues]) =>
      form.setValue(name, value as never, {
        shouldValidate: true,
        shouldDirty: true,
      });
    // Identity is never overwritten — only filled in when blank. A stray click
    // on the overlay must not silently change who the booking is for (AHD-9698).
    const setIdentityIfBlank = (
      name: keyof FormValues,
      value: FormValues[keyof FormValues],
    ) => {
      if (form.getValues(name)) return;
      set(name, value);
    };
    setIdentityIfBlank("firstName", "John");
    setIdentityIfBlank("lastName", "Doe");
    // Leave email untouched so you type a real address (no "e2e+" marker means
    // the submit function runs the full Relay webhook path).
    setIdentityIfBlank("birthday", "15.06.1990");
    setIdentityIfBlank("genderAtBirth", "male");
    set("phone", isGermany ? "+4915112345678" : "+41791234567");
    set("street", isGermany ? "Marienplatz" : "Bahnhofstrasse");
    set("streetNumber", "1");
    set("zipCode", isGermany ? "80331" : "8001");
    set("city", isGermany ? "München" : "Zürich");
    set("country", defaultCountryCode);
    set("gdprConsent", true);
    set("tosAndPpConsent", true);
    if (market === "ch") {
      // The CH consent record is stamped at the tick (MedicalConsentField); the
      // dev fill bypasses the click, so stamp it here too, or a dev/staging
      // booking records a guessed variant and no label (AHD-12757).
      set("chGtcConsentAt", new Date().toISOString());
      set(
        "chGtcConsentVariant",
        includeMembershipTier ? "gtc_membership" : "gtc_only",
      );
      set(
        "chGtcConsentLabel",
        document
          .getElementById("tosAndPpConsent-label")
          ?.textContent?.replace(/\s+/g, " ")
          .trim(),
      );
    }
    set("medicalProcessing", true);
    set("anonymousDataUsage", true);
    if (isGermany) {
      set("deWunschleistungConsent", true);
      set("atLabAndDoctorConsent", true);
      set("atConfidentialityConsent", true);
      set("atWithdrawalConsent", true);
      set("deTreatmentConsent", true);
      set("deConfidentialityConsent", true);
    }
  };

  const fillFormButton = isDevHost && form && (
    <button
      type="button"
      onClick={fillTestData}
      className="fixed bottom-16 left-4 z-[60] rounded-full bg-black/80 text-white text-xs font-medium normal-case px-3 py-2 shadow-lg hover:bg-black transition-colors"
    >
      Fill form (dev)
    </button>
  );

  const personalInfoCard = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Row 1: First Name | Last Name (NameField spans 2 columns) */}
      <div className="md:col-span-2">
        <NameField
          register={register}
          errors={errors}
          t={t}
          userName={userName}
          lockedFirstName={lockedFirstName}
          lockedLastName={lockedLastName}
        />
      </div>

      {/* Row 2: Birthday | Gender */}
      <BirthdayField
        control={control}
        errors={errors}
        t={t}
        validateBirthday={validateBirthday}
      />

      <GenderField control={control} errors={errors} t={t} />

      {/* Row 3: Phone | Email */}
      <PhoneField
        control={control}
        t={t}
        language={language}
        defaultCountry={defaultCountryCode as Country}
        placeholder={phonePlaceholder}
      />

      <EmailField
        control={control}
        t={t}
        userEmail={userEmail}
        register={register}
        errors={errors}
        locked={lockedEmail}
      />

      {/* Row 4: Language (full width) */}
      <div className="md:col-span-2 space-y-2">
        <LanguageField control={control} t={t} />
      </div>

      {/* Row 4b: Anxious Patient checkbox (full width) - only when blood work included */}
      {hasBloodWork && (
        <div className="md:col-span-2">
          <AnxiousPatientField control={control} t={t} />
        </div>
      )}

      {/* Row 5: Notes | Invitation Code */}
      <NotesField register={register} t={t} />

      <InvitationCodeInput language={language} items={items} />
    </div>
  );

  const addressFields = (
    <AddressFields
      register={register}
      errors={errors}
      t={t}
      control={control}
      language={language}
      defaultCountry={defaultCountryCode}
    />
  );

  const consentFields = (
    <MedicalConsentField
      t={t}
      errors={errors}
      control={control}
      setValue={form?.setValue}
      // "A membership SKU is in the cart": the same flag that decides whether
      // the tier is charged, so the consent label and the invoice agree.
      membershipSelected={includeMembershipTier}
    />
  );

  const stepBadge = (num: number, active: boolean) => (
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm font-medium ${
        active
          ? "bg-terracotta-600 text-white"
          : "bg-terracotta-100 text-terracotta-400"
      }`}
    >
      {num}
    </span>
  );

  // ── Unified details layout (Superpower-style numbered sections) ──────────────
  // Used for EVERY flow: membership-first (path A) and the blood-test one-off +
  // upsell (path B) share one shell — section 1 (personal) + section 2 (address)
  // + consents. The inline payment section (#3) only appears for the path-B
  // variant-4 upsell (showPaymentBlock). Path A, existing members and variant 3
  // stop at the consents and pay on the next step (Stripe via the sidebar CTA).
  return (
    <div className="space-y-4">
      {fillFormButton}

      {/* Section 1 — Personal information */}
      <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
        <div className="flex items-center gap-3">
          {stepBadge(1, true)}
          <div>
            <h3 className="text-xl font-medium text-gray-900">
              {i18n("bookingForm.personalInfo")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {i18n("bookingForm.personalInfoSubheader")}
            </p>
          </div>
        </div>
        {personalInfoCard}
      </div>

      {/* Section 2 — Address */}
      <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
        <div className="flex items-center gap-3">
          {stepBadge(2, true)}
          <h3 className="text-xl font-medium text-gray-900">
            {i18n("bookingForm.address")}
          </h3>
        </div>
        {addressFields}
      </div>

      {/* Inline payment section (#3) — only the path-B upsell. Path A and
          existing members skip it (payment is the next step). */}
      {showPaymentBlock && (
        <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            {stepBadge(3, true)}
            <h3 className="text-xl font-medium text-gray-900">
              {/* With a membership option it's a real choice ("Select payment
                  type"); without one (e.g. MRI) there's nothing to pick, so the
                  header states the outcome ("No payment required"). */}
              {offerMembership
                ? i18n("paymentInfo.selectPaymentType")
                : i18n("paymentInfo.title")}
            </h3>
          </div>
          <PaymentInfo
            language={language}
            hasBloodComponent={offerMembership}
            hideTitle
          />
        </div>
      )}

      {/* Same slot, same badge — the membership was already chosen, so this
          states the payment rather than asking for it. */}
      {showMemberPaymentSummary && (
        <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            {stepBadge(3, true)}
            <h3 className="text-xl font-medium text-gray-900">
              {i18n("paymentInfo.yourPayment")}
            </h3>
          </div>
          <MemberPaymentSummary language={language as BookingLanguage} />
        </div>
      )}

      {/* Legacy standalone payment card — restored for DE / flag-off (the
          feature is gated off, so the inline variant-4 section above never
          renders). Hidden for existing members. */}
      {!membershipFlowEnabled && !isMember && (
        <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
          <PaymentInfo language={language} />
        </div>
      )}

      {/* Agreement consents — sit below on the page background (no card),
          indented (px-6) to line up under the section badges. */}
      {!isInternal && <div className="px-6">{consentFields}</div>}
    </div>
  );
};
